import React, { useState, useEffect } from "react";
import { Table, Layout, Flex, Typography, Button, notification, Form, Input, Avatar, Tabs, Modal } from "antd";
import { EditOutlined, PlusOutlined, CloseCircleOutlined } from "@ant-design/icons";
import "./styles.scss";
import Uploader from "components/ImageUpload/AvatarUploader";
import {
	useCreateCoachMutation,
	useGetDomainGymQuery,
	useGetCoachesQuery,
	useUpdateCoachMutation,
} from "store/api/api";
import { useParams } from "react-router";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";

const Trainers = () => {
	const { gym } = useParams();
	const [modal, contextHolder] = Modal.useModal();
	const { data: gymData } = useGetDomainGymQuery(gym, { skip: !gym });
	const {
		data: coachesData,
		isLoading: isCoachesLoading,
		refetch,
	} = useGetCoachesQuery(gymData?.id, {
		skip: !gymData?.id,
	});
	const [createCoachTrigger, createCoachResponse] = useCreateCoachMutation();
	const [updateCoachTrigger, updateCoachResponse] = useUpdateCoachMutation();
	const [activeTrainer, setActiveTrainer] = useState(null);

	const data = coachesData?.coaches;

	useEffect(() => {
		if (!isCoachesLoading && data.length) {
			refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refetch]);
	const handleEdit = (vals) => {
		setActiveTrainer(vals);
		setIsModalVisible(true);
		form.setFieldsValue({
			name: vals.name,
		});
	};

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [form] = Form.useForm();

	const handleCancel = async () => {
		setIsModalVisible(false);
		setActiveTrainer(null);
		setCropStatus(false);
		form.setFieldValue("name", "");
	};

	const [cropStatus, setCropStatus] = useState(false);
	const [croppedImage, setCroppedImage] = useState(null);

	const handleToggle = async (record) => {
		try {
			const updatedStatus = record.status === "active" ? "inactive" : "active";
			const formData = new FormData();
			formData.append("status", updatedStatus);
			await updateCoachTrigger({ body: formData, coachId: record.id }).unwrap();
			notification.success({
				message: `Trainer ${updatedStatus === "active" ? "activated" : "inactivated"} successfully!`,
			});
			refetch();
		} catch (error) {
			console.error("Failed to update trainer status:", error);
			notification.error({
				message: error?.data?.message || "Failed to update trainer status. Please try again.",
			});
		}
	};
	const handleSubmit = async (values) => {
		try {
			const formData = new FormData();
			formData.append("gym_id", gymData?.id);
			croppedImage && formData.append("image", croppedImage);
			if (activeTrainer) {
				formData.append("id", activeTrainer.id);
				if (values.name !== activeTrainer.name) formData.append("name", values.name);
				await updateCoachTrigger({ body: formData, coachId: activeTrainer.id }).unwrap();
				notification.success({
					message: "Trainer updated successfully!",
				});
			} else {
				formData.append("name", values.name);
				await createCoachTrigger(formData).unwrap();
				notification.success({
					message: "Trainer created successfully!",
				});
			}

			await refetch();
			setIsModalVisible(false);
			form.setFieldValue("name", "");
			setActiveTrainer(null);
			setCroppedImage(null);
		} catch (error) {
			console.error("Failed to create coach:", error);
			notification.error({
				message: error?.data?.message || "Failed to create coach. Please try again.",
			});
		}
	};

	const primaryColor = usePrimaryColor();
	const isMobile = window.innerWidth <= 768;

	const onConfirm = async (record) => {
		await handleToggle(record);
		await refetch();
	};
	const onActivate = (record) => {
		modal.confirm({
			title: "Confirm",
			content: `Are you sure you want to ${record.status === "active" ? "Inactivate" : "Activate"} this trainer?`,
			okText: "Confirm",
			cancelText: "Go Back",
			okButtonProps: {
				className: "primary-filled need-help-btn",
				type: "primary",
			},
			cancelButtonProps: {
				className: "primary-filled need-help-btn",
				type: "default",
			},
			icon: null,
			onOk: () => onConfirm(record),
		});
	};
	const columns = [
		{
			title: "Trainer Image",
			dataIndex: "picture_url",
			key: "image",
			render: (text) => <Avatar size={40} src={text} />,
			width: 120,
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			showSorterTooltip: { target: "full-header" },
			onFilter: (value, record) => record.name.indexOf(value) === 0,
			sorter: (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
			sortDirections: ["ascend", "descend"],
			defaultSortOrder: "ascend",
		},
		{
			title: "Number of Workouts",
			dataIndex: "number_of_workouts",
			key: "number_of_workouts",
			width: 160,
		},
		{
			title: "Actions",
			key: "actions",
			fixed: "right",
			width: 100,
			render: (text, record) => (
				<Flex gap={16} align="center">
					<Button
						icon={<EditOutlined />}
						className="need-help-btn"
						size="small"
						variant="outlined"
						onClick={() => handleEdit(record)}
						style={{
							width: isMobile ? 32 : "max-content",
							minWidth: isMobile ? 32 : "unset",
							color: primaryColor,
							borderColor: primaryColor,
						}}
					>
						{isMobile ? "" : "Edit"}
					</Button>
					<Button
						icon={<CloseCircleOutlined />}
						className="need-help-btn"
						size="small"
						variant="outlined"
						style={{ borderColor: primaryColor, color: primaryColor, width: isMobile ? 32 : "max-content" }}
						onClick={() => onActivate(record)}
					>
						{isMobile ? "" : `${record.status === "active" ? "Inactivate" : "Activate"}`}
					</Button>
				</Flex>
			),
		},
	];

	const onCropDone = (imageSrc) => {
		setCroppedImage(imageSrc);
		setCropStatus(false);
	};

	return (
		<Layout className="sb-trainers-table">
			<Modal
				title={activeTrainer?.name ? "Edit Trainer" : "Add New Trainer"}
				open={isModalVisible}
				okText="Add"
				cancelText="Cancel"
				destroyOnClose
				footer={null}
				maskClosable={false}
				onCancel={handleCancel}
			>
				<Form
					initialValues={{
						name: activeTrainer?.name || "",
					}}
					onFinish={handleSubmit}
					form={form}
					layout="vertical"
				>
					<Form.Item
						label="Trainer Name"
						name="name"
						rules={[
							{
								required: true,
								validator: (_, value) => {
									if (!value?.trim?.()) {
										return Promise.reject("This field is required");
									}
									// name length must be less than or equal to 256 characters long
									if (value.length >= 256) {
										return Promise.reject("Maximum allowed characters is 255");
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<Input placeholder="Enter trainer name" />
					</Form.Item>
					<Uploader setCropStatus={setCropStatus} initialImage={activeTrainer?.picture_url} onCropDone={onCropDone} />
					{!cropStatus && (
						<Flex
							className="sb-confirm-form__actions"
							justify="space-between"
							style={{ marginTop: 16, marginBottom: 16 }}
						>
							<Button htmlType="button" onClick={handleCancel}>
								Cancel
							</Button>
							<Button
								loading={createCoachResponse?.isLoading || updateCoachResponse?.isLoading}
								type="primary"
								variant="filled"
								htmlType="submit"
							>
								{activeTrainer ? "Save Changes" : "Create Trainer"}
							</Button>
						</Flex>
					)}
				</Form>
			</Modal>
			<Flex justify="space-between" align="center" style={{ marginBottom: "20px" }}>
				<Typography.Title level={3}>Trainers</Typography.Title>
				<Button icon={<PlusOutlined />} type="primary" onClick={() => setIsModalVisible(true)}>
					New Trainer
				</Button>
			</Flex>
			<Tabs
				defaultActiveKey="active"
				items={[
					{
						label: "Active",
						key: "active",
						children: (
							<Table
								size="small"
								loading={isCoachesLoading}
								columns={columns}
								dataSource={data?.filter((trainer) => trainer.status === "active")}
								pagination={{ pageSize: 10 }}
								scroll={{ x: "max-content" }}
								rowKey={(record) => record.id}
							/>
						),
					},
					{
						label: "Inactive",
						key: "inactive",
						children: (
							<Table
								size="small"
								loading={isCoachesLoading}
								columns={columns}
								dataSource={data?.filter((trainer) => trainer.status === "inactive")}
								pagination={{ pageSize: 10 }}
								scroll={{ x: "max-content" }}
								rowKey={(record) => record.id}
							/>
						),
					},
				]}
			/>
			{contextHolder}
		</Layout>
	);
};

export default Trainers;

